import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Badge } from 'react-bootstrap';

const SETTLE_MS = 400;
const DEBOUNCE_MS = 1500;

export function useGuideDocumentAutosave({ html, enabled, resetKey, persist }) {
  const [status, setStatus] = useState('saved');
  const [savedAt, setSavedAt] = useState(null);

  const htmlRef = useRef(html);
  htmlRef.current = html;
  const persistRef = useRef(persist);
  persistRef.current = persist;
  const flushPersistRef = useRef(persist);
  const savedHtmlRef = useRef(html);
  const timerRef = useRef(0);
  const readyRef = useRef(false);
  const inFlightRef = useRef(false);
  const retryRef = useRef(false);
  const genRef = useRef(0);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const saveNow = useCallback(async () => {
    const toSave = htmlRef.current;
    if (toSave === savedHtmlRef.current) {
      setStatus('saved');
      return;
    }
    if (inFlightRef.current) {
      retryRef.current = true;
      return;
    }
    if (timerRef.current) {
      window.clearTimeout(timerRef.current);
      timerRef.current = 0;
    }
    const gen = genRef.current;
    inFlightRef.current = true;
    setStatus('saving');
    try {
      const result = await persistRef.current(toSave);
      if (!mountedRef.current || gen !== genRef.current) return;
      if (htmlRef.current === toSave) {
        savedHtmlRef.current = result?.html ?? toSave;
      } else {
        savedHtmlRef.current = toSave;
      }
      setSavedAt(new Date());
      if (htmlRef.current === savedHtmlRef.current || htmlRef.current === result?.html) {
        savedHtmlRef.current = htmlRef.current;
        setStatus('saved');
      } else {
        setStatus('unsaved');
        retryRef.current = true;
      }
    } catch (err) {
      if (mountedRef.current && gen === genRef.current) setStatus('error');
      throw err;
    } finally {
      inFlightRef.current = false;
      if (retryRef.current && mountedRef.current && gen === genRef.current) {
        retryRef.current = false;
        timerRef.current = window.setTimeout(() => {
          void saveNow().catch(() => {});
        }, DEBOUNCE_MS);
      }
    }
  }, []);

  useEffect(() => {
    genRef.current += 1;
    readyRef.current = false;
    if (timerRef.current) {
      window.clearTimeout(timerRef.current);
      timerRef.current = 0;
    }
    savedHtmlRef.current = htmlRef.current;
    setStatus('saved');
    if (!enabled) return undefined;
    const t = window.setTimeout(() => {
      savedHtmlRef.current = htmlRef.current;
      readyRef.current = true;
    }, SETTLE_MS);
    return () => window.clearTimeout(t);
  }, [resetKey, enabled]);

  useEffect(() => {
    if (!enabled || !readyRef.current) {
      if (!readyRef.current) savedHtmlRef.current = html;
      return undefined;
    }
    if (html === savedHtmlRef.current) {
      if (!inFlightRef.current) {
        setStatus((prev) => (prev === 'error' || prev === 'saving' ? prev : 'saved'));
      }
      return undefined;
    }
    setStatus('unsaved');
    if (timerRef.current) window.clearTimeout(timerRef.current);
    timerRef.current = window.setTimeout(() => {
      void saveNow().catch(() => {});
    }, DEBOUNCE_MS);
    return () => {
      if (timerRef.current) window.clearTimeout(timerRef.current);
    };
  }, [html, enabled, saveNow]);

  useEffect(() => {
    return () => {
      if (timerRef.current) window.clearTimeout(timerRef.current);
      if (htmlRef.current !== savedHtmlRef.current) {
        void flushPersistRef.current(htmlRef.current).catch(() => {});
      }
    };
  }, [resetKey]);

  useEffect(() => {
    flushPersistRef.current = persist;
  }, [resetKey, persist]);

  useEffect(() => {
    const flush = () => {
      if (htmlRef.current === savedHtmlRef.current) return;
      void persistRef.current(htmlRef.current).catch(() => {});
    };
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') flush();
    };
    window.addEventListener('pagehide', flush);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.removeEventListener('pagehide', flush);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, []);

  return { status, savedAt, saveNow };
}

function formatSavedAt(date) {
  if (!date) return 'Saved';
  try {
    return `Saved ${date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}`;
  } catch {
    return 'Saved';
  }
}

export function GuideAutosaveIndicator({ status, savedAt }) {
  const label =
    status === 'saving'
      ? 'Saving…'
      : status === 'unsaved'
        ? 'Unsaved'
        : status === 'error'
          ? 'Save failed'
          : formatSavedAt(savedAt);

  const icon =
    status === 'saving'
      ? 'bi-arrow-repeat'
      : status === 'unsaved'
        ? 'bi-pencil'
        : status === 'error'
          ? 'bi-exclamation-circle'
          : 'bi-check-circle';

  const variant =
    status === 'error' ? 'danger' : status === 'unsaved' ? 'secondary' : 'success';

  return (
    <Badge
      bg={variant}
      className={`guides-autosave-status is-${status}`}
      role="status"
      aria-live="polite"
    >
      <i className={`bi ${icon}`} aria-hidden="true" />
      {label}
    </Badge>
  );
}
